import { useEffect, useMemo, useRef } from 'react';

import spritesheet from './spritesheet.json';

interface WeegioAnimationProps {
  className?: string;
  width?: number;
  height?: number;
  fps?: number;
}

/**
 * WeegioAnimation component that renders an animated sprite using canvas
 * @param className - Optional CSS class for styling
 * @param width - Canvas width in pixels (defaults to client width or 284px)
 * @param height - Canvas height in pixels (defaults to client height or 272px)
 * @param fps - Animation frame rate (defaults to 18 fps)
 */
export default function WeegioAnimation({
  className,
  width,
  height,
  fps = 18,
}: WeegioAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Prepare ordered frames from spritesheet JSON
  const frames = useMemo(() => {
    const entries = Object.entries((spritesheet as any).frames ?? {});
    entries.sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
    return entries.map(
      ([, value]) => (value as any).frame as { x: number; y: number; w: number; h: number },
    );
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || frames.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Narrow types for use inside animation loop
    const assuredCanvas = canvas as HTMLCanvasElement;
    const assuredCtx = ctx as CanvasRenderingContext2D;

    const image = new window.Image();
    image.src =
      'https://zen.wego.com/cdn-cgi/image/format=auto,quality=95/web/weegio/weegio-homepage-spritesheet.png';
    let isCancelled = false;

    const targetCssWidth = width || assuredCanvas.clientWidth || 284; // fallback to ~17.75rem
    const targetCssHeight = height || assuredCanvas.clientHeight || 272; // approximate height preserving aspect

    // Set the internal resolution to match CSS size for sharper rendering
    const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
    assuredCanvas.width = Math.floor(targetCssWidth * dpr);
    assuredCanvas.height = Math.floor(targetCssHeight * dpr);
    assuredCtx.scale(dpr, dpr);

    const frameInterval = 1000 / fps;
    let lastTime = 0;
    let frameIndex = 0;

    function draw(time: number) {
      if (isCancelled) return;
      if (!lastTime) lastTime = time;
      const delta = time - lastTime;
      if (delta >= frameInterval) {
        lastTime = time;
        const f = frames[frameIndex];
        // Clear canvas
        assuredCtx.clearRect(0, 0, assuredCanvas.width, assuredCanvas.height);
        // Compute destination size preserving aspect ratio to fit canvas CSS box
        const destWidth = targetCssWidth;
        const destHeight = (f.h / f.w) * destWidth;
        const offsetY = Math.max(0, (targetCssHeight - destHeight) / 2);
        assuredCtx.drawImage(image, f.x, f.y, f.w, f.h, 0, offsetY, destWidth, destHeight);
        frameIndex = (frameIndex + 1) % frames.length;
      }
      requestAnimationFrame(draw);
    }

    image.onload = () => {
      if (!isCancelled) requestAnimationFrame(draw);
    };

    return () => {
      isCancelled = true;
    };
  }, [frames, width, height, fps]);

  return <canvas ref={canvasRef} className={className} />;
}
